const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
const LOAD_TIMEOUT_MS = 5000;

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          callback: (t: string) => void;
          'error-callback'?: (code?: string) => void;
          'timeout-callback'?: () => void;
        }
      ) => string;
      reset: (id?: string) => void;
    };
  }
}

// Backstop for the render→token round-trip. Turnstile's own error-callback
// usually fires within a second on a hard failure (e.g. the page's domain isn't
// in the widget's allowed-hostnames list — error 110200), but if no callback
// fires at all we must still reject rather than leave the chat spinning forever.
const TOKEN_TIMEOUT_MS = 12000;

let loadPromise: Promise<void> | null = null;

export function loadTurnstileScript(): Promise<void> {
  if (loadPromise) return loadPromise;
  loadPromise = new Promise((res, rej) => {
    const existing = document.querySelector(`script[src="${SCRIPT_URL}"]`);
    if (existing) {
      if (window.turnstile) return res();
      existing.addEventListener('load', () => res());
      existing.addEventListener('error', () => rej(new Error('turnstile load error')));
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_URL;
    s.async = true;
    s.defer = true;
    const timer = setTimeout(() => rej(new Error('turnstile timeout')), LOAD_TIMEOUT_MS);
    s.addEventListener('load', () => {
      clearTimeout(timer);
      res();
    });
    s.addEventListener('error', () => {
      clearTimeout(timer);
      rej(new Error('turnstile load error'));
    });
    document.head.appendChild(s);
  });
  return loadPromise;
}

export async function getTurnstileToken(siteKey: string, container: HTMLElement): Promise<string> {
  await loadTurnstileScript();
  if (!window.turnstile) throw new Error('turnstile not loaded');
  return new Promise<string>((resolve, reject) => {
    let settled = false;
    const finish = (fn: () => void): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      fn();
    };
    const timer = setTimeout(
      () => finish(() => reject(new Error('turnstile timeout'))),
      TOKEN_TIMEOUT_MS
    );
    try {
      window.turnstile!.render(container, {
        sitekey: siteKey,
        callback: (token: string) => finish(() => resolve(token)),
        // Without these, a Turnstile failure (disallowed domain, network error,
        // or an interactive challenge that can't be solved in our hidden
        // container) never calls `callback`, so the promise — and the chat —
        // would hang indefinitely. Reject instead so the caller shows an error.
        'error-callback': () => finish(() => reject(new Error('turnstile error'))),
        'timeout-callback': () => finish(() => reject(new Error('turnstile challenge timeout')))
      });
    } catch (err) {
      finish(() => reject(err instanceof Error ? err : new Error('turnstile render failed')));
    }
  });
}
